import * as React from "react";
import { Button } from "../components/ui/button";
import { skills as mockSkills } from "../data/skills";
import { auditSkill } from "../services/skillAudit";
import { notifySkillApproved, notifySkillRejected } from "../services/emailNotifications";
import Footer from "../components/Footer";

import { useTranslation } from 'react-i18next';
const STATUS_LABELS = { aprovado: "Approved", rejeitado: "Rejected", fila_revisao: "In Review" };
const STATUS_COLORS = { aprovado: "text-green-400 bg-green-900/20", rejeitado: "text-red-400 bg-red-900/20", fila_revisao: "text-yellow-400 bg-yellow-900/20" };

export default function AuditPanel() {
  const { t } = useTranslation();
  const [skillList, setSkillList] = React.useState(mockSkills);
  const [auditLog, setAuditLog] = React.useState([]);
  const [filter, setFilter] = React.useState("all");
  const [toasts, setToasts] = React.useState([]);

  const pending = skillList.filter((s) => !s.approved);
  const approved = skillList.filter((s) => s.approved);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, ...toast }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, toast.duration || 5000);
  };

  const handleApprove = (skill) => {
    const result = auditSkill({ ...skill, description: skill.description || "Manually approved", steps: skill.steps || "step1\nstep2\nstep3\nstep4" });
    setSkillList((prev) => prev.map((s) => s.id === skill.id ? { ...s, approved: true, auditResult: result } : s));
    setAuditLog((prev) => [{ action: "Approved", skill: skill.name, time: new Date().toLocaleTimeString() }, ...prev]);

    // Trigger email notification (mock)
    const notification = notifySkillApproved(skill.name, skill.creatorEmail);
    addToast({ type: notification.type, title: notification.title, message: notification.message, duration: 6000 });
  };

  const handleReject = (skill) => {
    const result = { status: "rejeitado", feedback: "Rejected by auditor" };
    setSkillList((prev) => prev.map((s) => s.id === skill.id ? { ...s, approved: false, rejected: true, auditResult: result } : s));
    setAuditLog((prev) => [{ action: "Rejected", skill: skill.name, time: new Date().toLocaleTimeString() }, ...prev]);

    // Trigger email notification (mock)
    const notification = notifySkillRejected(skill.name, skill.creatorEmail, "Rejected by auditor");
    addToast({ type: notification.type, title: notification.title, message: notification.message, duration: 6000 });
  };

  const displayed = filter === "pending" ? pending : filter === "approved" ? approved : skillList;

  return (
    <div className="p-6 text-white max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">{t('audit_panel.audit_panel')}</h2>
          <p className="text-sm text-slate-400 mt-1">{pending.length} pending | {approved.length} approved</p>
        </div>
        <div className="flex gap-2">
          {["all", "pending", "approved"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded text-sm ${filter === f ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 border border-slate-600"}`}>
              {f === "all" ? "All" : f === "pending" ? "Pending" : "Approved"}
            </button>
          ))}
        </div>
      </div>

      <table className="w-full border-collapse border border-slate-700 rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-slate-800">
            <th className="border border-slate-700 p-3 text-left text-sm">{t('audit_panel.skill')}</th>
            <th className="border border-slate-700 p-3 text-left text-sm">{t('audit_panel.creator')}</th>
            <th className="border border-slate-700 p-3 text-left text-sm">{t('audit_panel.category')}</th>
            <th className="border border-slate-700 p-3 text-left text-sm">{t('audit_panel.status')}</th>
            <th className="border border-slate-700 p-3 text-left text-sm">{t('audit_panel.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map((s) => (
            <tr key={s.id} className="border-b border-slate-700 hover:bg-slate-800/50">
              <td className="border border-slate-700 p-3 text-sm">{s.name}</td>
              <td className="border border-slate-700 p-3 text-sm">{s.author}</td>
              <td className="border border-slate-700 p-3 text-sm">{s.category}</td>
              <td className="border border-slate-700 p-3 text-sm">
                <span className={`px-2 py-0.5 rounded text-xs ${s.approved ? STATUS_COLORS.aprovado : s.rejected ? STATUS_COLORS.rejeitado : STATUS_COLORS.fila_revisao}`}>
                  {s.approved ? "Approved" : s.rejected ? "Rejected" : "Pending"}
                </span>
              </td>
              <td className="border border-slate-700 p-3">
                {!s.approved && !s.rejected && (
                  <>
                    <Button size="sm" className="mr-2 bg-green-600 hover:bg-green-700" onClick={() => handleApprove(s)}>Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => handleReject(s)}>Reject</Button>
                  </>
                )}
                {s.approved && <span className="text-xs text-slate-500">{t('audit_panel.processed')}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {auditLog.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-bold text-slate-300 mb-2">{t('audit_panel.log_de_auditoria')}</h3>
          <div className="bg-slate-800 rounded-lg p-3 max-h-40 overflow-y-auto">
            {auditLog.map((log, i) => (
              <div key={i} className="text-xs text-slate-400 py-1 border-b border-slate-700/50">
                <span className={log.action === "Approved" ? "text-green-400" : "text-red-400"}>{log.action}</span> "{log.skill}" -- {log.time}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inline toast notifications for email mock */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        {toasts.map((toast) => {
          const typeStyles = {
            success: "bg-green-900/40 border-green-600 text-green-300",
            error: "bg-red-900/40 border-red-600 text-red-300",
            info: "bg-blue-900/40 border-blue-600 text-blue-300",
            warning: "bg-yellow-900/40 border-yellow-600 text-yellow-300",
          };
          const typeIcons = { success: "✓", error: "✕", info: "ℹ", warning: "⚠" };
          return (
            <div
              key={toast.id}
              className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg min-w-[300px] max-w-[420px] ${typeStyles[toast.type] || typeStyles.info}`}
            >
              <span className="text-lg flex-shrink-0">{typeIcons[toast.type] || typeIcons.info}</span>
              <div className="flex-1 min-w-0">
                {toast.title && <div className="font-semibold text-sm mb-0.5">{toast.title}</div>}
                <div className="text-sm opacity-90">{toast.message}</div>
              </div>
              <button onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))} className="text-white/50 hover:text-white flex-shrink-0">
                ✕
              </button>
      <Footer />

            </div>
          );
        })}
      </div>
    </div>
  );
};
